import {ChevronDownIcon, MinusIcon, PlusIcon} from '@heroicons/react/outline';
import type {JSX, ReactNode} from 'react';
import {forwardRef, useEffect, useId, useRef, useState} from 'react';

import {cn} from '@/utils/cn';
import {formatWithCommas} from '@/utils/commaFormatter';

interface InputProps {
  label?: string;
  placeholder?: string;
  className?: string;
  // Read by CheckBox below, so it needs a real type rather than the index one.
  checked?: boolean;
  // Open-ended so callers can pass through any <input> DOM attribute;
  // `unknown` still forwards cleanly through the spread.
  [rest: string]: unknown;
}

interface NumberInputProps extends InputProps {
  onValueChange?: (
    rawValue: string,
    e?: React.ChangeEvent<HTMLInputElement>,
  ) => void; // optional callback for raw number
  // Overrides the InputProps index signature: every call site passes either
  // a number or a formatted numeric string.
  value?: string | number;
}
interface MultiSelectProps {
  name: string;
  options?: {key: string; value: string}[];
  onChange: (p: unknown[]) => void;
  prompt?: string;
  label?: string;
  error?: string;
  value?: unknown[];
}

export interface CInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Test hook placed on the `<input>` itself, matching Select/CheckBox. */
  dataCy?: string;
}

export interface TextAreaProps extends React.InputHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export interface SelectProps {
  label?: string;
  placeholder?: string;
  className?: string;
  options: {key: string; value: string; disabled?: boolean}[];
  bodyClassName?: string;
  dataCy?: string;
  // Read by Select below, so it needs a real type rather than the index one.
  error?: string;
  // Open-ended so callers can pass through any <select> DOM attribute;
  // `unknown` still forwards cleanly through the spread.
  [key: string]: unknown;
}

export const Input = forwardRef<HTMLInputElement, CInputProps>(
  (
    {
      type = 'text',
      label,
      error,
      className,
      leftIcon,
      rightIcon,
      dataCy,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        className={`custom-input-autofill ${className?.includes('isFull') ? 'w-full' : ''}`}
      >
        {label && (
          <label
            htmlFor={props.id}
            className={`block text-sm font-medium mb-1 ${
              props.disabled
                ? 'text-gray-500 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-700 dark:text-gray-200'
            }`}
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            data-cy={dataCy}
            {...props}
            type={type}
            className={`block ${
              leftIcon ? 'pl-10' : 'pl-3'
            } text-sm rounded-md focus:ring focus:ring-opacity-50 ${
              error ? 'border-red-500' : ''
            } ${
              props.disabled
                ? 'bg-gray-100 dark:bg-gray-900 border-gray-200 dark:border-dark-borderHeavy text-gray-500 dark:text-gray-600 cursor-not-allowed'
                : 'border-gray-300 dark:border-dark-borderHeavy bg-white dark:bg-transparent dark:focus:bg-transparent focus:border-indigo-300 focus:ring-indigo-200 dark:text-gray-200'
            } ${className}`}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none z-20">
              {rightIcon}
            </div>
          )}
        </div>
        {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
      </div>
    );
  },
);
Input.displayName = 'Input';

export const NumberInput: React.FC<NumberInputProps> = ({
  onValueChange,
  value: initialValue,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState(
    formatWithCommas(String(initialValue ?? '')),
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    //allow digits and at most one dot
    const numericValue = rawValue
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1');

    setDisplayValue(formatWithCommas(numericValue));

    if (onValueChange) {
      onValueChange(numericValue, e); //send clean numeric value (with decimals preserved) upstream
    }
  };

  //only sync when parent actually changes the value
  useEffect(() => {
    const formatted = formatWithCommas(String(initialValue ?? ''));
    if (formatted !== displayValue) {
      setDisplayValue(formatted);
    }
  }, [initialValue]);

  return (
    <Input
      {...props}
      type="text"
      value={displayValue}
      onChange={handleChange}
    />
  );
};
/**
 * `value` reaches Select through its open index signature, and some call sites
 * pass a shape a <select> cannot represent (an ICurrency object in the
 * transaction filters, a string[] in CreatePlanModal, null in the KYB/KYC
 * modals). Those are pre-existing call-site bugs, so this reproduces exactly
 * what the previous `value || ''` handed the DOM rather than quietly changing
 * them: falsy collapses to '', primitives and arrays pass through, and anything
 * else is stringified the way the DOM would have stringified it anyway.
 */
const toSelectValue = (value: unknown): string | number | readonly string[] => {
  if (!value) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  if (Array.isArray(value)) {
    // `Array.isArray` narrows to `any[]` (a long-standing lib.es5.d.ts gap),
    // not `unknown[]`; cast to the shape this branch is documented to carry
    // (a string[] from CreatePlanModal) rather than return the `any`.
    return value as readonly string[];
  }
  // Only a genuine object reaches this branch (string/number/array/falsy are
  // all handled above), so `String()` would silently render `[object Object]`.
  // JSON.stringify surfaces the actual bad value instead.
  return JSON.stringify(value);
};

export const Select: React.FC<SelectProps> = ({
  options,
  label,
  placeholder,
  value,
  className,
  bodyClassName,
  dataCy,
  error,
  ...props
}) => {
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
          {label}
        </label>
      )}
      <select
        data-cy={dataCy ? `${dataCy}-selector-options` : undefined}
        {...props}
        value={toSelectValue(value)}
        className={`block w-full pl-3 pr-10 py-2 text-gray-700 border-gray-300 dark:border-dark-borderHeavy dark:bg-transparent focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm rounded-md cursor-pointer ${bodyClassName} appearance-none disabled:cursor-not-allowed dark:focus:ring-0 dark:!text-gray-100`}
      >
        <option
          value=""
          disabled
          className="dark:text-gray-100 dark:bg-dark-inputBg"
        >
          {placeholder}
        </option>

        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            className="dark:text-gray-100 dark:bg-dark-inputBg"
          >
            {option.key}
          </option>
        ))}
      </select>
      {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
    </div>
  );
};

export function CheckBox({
  label,
  className,
  checked,
  dataCy,
  id,
  disabled = false,
  labelClassName,
  ...rest
}: InputProps & {
  id?: string;
  dataCy?: string;
  disabled?: boolean;
  labelClassName?: string;
}): JSX.Element {
  // Use internal state to handle animation smoothly
  const [isHovered, setIsHovered] = useState(false);

  // Generate a unique ID if none is provided. useId is stable across renders,
  // unlike the Math.random() fallback it replaces, which minted a fresh id on
  // every render and briefly broke the label/input association.
  const generatedId = useId();
  const checkboxId = id || `checkbox-${generatedId}`;

  const getBoxStateClassName = (): string => {
    if (checked) {
      return 'bg-green-600 border-green-600';
    }
    if (isHovered && !disabled) {
      return 'border-green-600 bg-green-50';
    }
    return 'border-gray-300 bg-white';
  };

  return (
    <label
      htmlFor={checkboxId}
      data-cy={dataCy ? `${dataCy}-checkbox-label` : undefined}
      className={cn(
        'flex items-center mb-3 select-none cursor-pointer',
        labelClassName,
      )}
    >
      <div
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <input
          id={checkboxId}
          data-cy={dataCy ? `${dataCy}-checkbox` : undefined}
          type="checkbox"
          className="sr-only" // Hide the default checkbox
          checked={checked}
          disabled={disabled}
          {...rest}
        />

        {/* Custom checkbox design */}
        <div
          className={`
            w-5 h-5 border-2 rounded-md flex items-center justify-center transition-all duration-200
            ${isHovered && !disabled ? 'shadow-md' : ''}
            ${getBoxStateClassName()}
            ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          {/* Checkmark icon */}
          {checked && (
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
                d="M5 13l4 4L19 7"
              ></path>
            </svg>
          )}
        </div>
      </div>

      <span
        className={`
          ml-2 font-medium transition-colors duration-200
          ${className?.includes('xs') ? 'text-xs' : 'text-sm'}
          ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 dark:text-gray-100'}
        `}
      >
        {label}
      </span>
    </label>
  );
}

export const ExpandToggle = ({
  isOpen,
  onClick,
  dataCy,
}: {
  isOpen: boolean;
  onClick: () => void;
  dataCy?: string;
}) => (
  <button
    data-cy={dataCy}
    onClick={(e: React.MouseEvent) => {
      e.preventDefault();
      onClick();
    }}
    className="p-1 hover:bg-slate-100 dark:hover:bg-dark-borderMid rounded text-slate-500 dark:text-gray-100 transition-colors"
  >
    {isOpen ? (
      <MinusIcon className="h-4 w-4" />
    ) : (
      <PlusIcon className="h-4 w-4" />
    )}
  </button>
);

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({label, error, className, ...props}, ref) => {
    return (
      <div className={className?.includes('isFull') ? 'w-full' : ''}>
        {label && (
          <label
            htmlFor={props.id}
            className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <textarea
            ref={ref}
            {...props}
            className={`block w-full pl-3 sm:text-sm border-gray-300 dark:border-dark-borderHeavy dark:bg-transparent rounded-md focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 font-normal ${
              error ? 'border-red-500' : ''
            } ${className}`}
            rows={props['aria-rowspan']}
          />
        </div>
        {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
      </div>
    );
  },
);
TextArea.displayName = 'TextArea';

/**
 * The component below implements multi select dropdown component
 * Reference: https://www.prudkohliad.com/articles/multi-select-dropdown-with-react-and-tailwind-4-2025-02-04
 */
export const MultiSelectDropdown = ({
  name,
  options = [],
  onChange,
  prompt = 'Select one or more options',
  label,
  error,
  value,
}: MultiSelectProps) => {
  const [selectedOptions, setSelectedOptions] = useState<unknown[]>(
    typeof value === 'undefined' ? [] : value,
  );
  const optionsListRef = useRef<HTMLUListElement | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    const option = e.target.value;
    const selectedOptionSet = new Set(selectedOptions);

    if (isChecked) {
      selectedOptionSet.add(option);
    } else {
      selectedOptionSet.delete(option);
    }

    const newSelectedOptions = Array.from(selectedOptionSet);

    setSelectedOptions(newSelectedOptions);
    onChange(newSelectedOptions);
  };

  const isSelectAllEnabled = selectedOptions.length < options.length;

  const handleSelectAllClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const optionsInputs =
      optionsListRef.current?.querySelectorAll<HTMLInputElement>('input');
    optionsInputs?.forEach((input) => {
      input.checked = true;
    });

    const valuesOnly = options.map((option) => option.value);
    setSelectedOptions(valuesOnly);
    onChange(valuesOnly);
  };

  const isClearSelectionEnabled = selectedOptions.length > 0;

  const handleClearSelectionClick = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();

    const optionsInputs =
      optionsListRef.current?.querySelectorAll<HTMLInputElement>('input');
    optionsInputs?.forEach((input) => {
      input.checked = false;
    });

    setSelectedOptions([]);
    onChange([]);
  };

  return (
    <div className="w-full relative z-20">
      {label && (
        <label
          className={`block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200`}
        >
          {label}
        </label>
      )}

      <div className="relative w-full">
        <input type="checkbox" id="toggle" className="peer hidden" />

        <label
          htmlFor="toggle"
          className="flex justify-between items-center w-full px-3 py-2 border border-gray-300 dark:border-dark-borderHeavy rounded-md cursor-pointer"
        >
          <span className="truncate">
            {prompt}
            {selectedOptions.length > 0 && (
              <span className="ml-1 text-blue-500 whitespace-nowrap">
                ({selectedOptions.length} selected)
              </span>
            )}
          </span>

          <span className="text-xs transition-transform duration-300 peer-checked:rotate-180">
            <ChevronDownIcon className="w-4 h-4 text-gray-500" />
          </span>
        </label>

        <div className="absolute bg-white dark:bg-dark-inputBg  transition-opacity opacity-0 pointer-events-none peer-checked:opacity-100 peer-checked:pointer-events-auto w-full max-h-60 overflow-y-auto border border-gray-300 dark:border-dark-borderHeavy rounded-tl-md rounded-tr-md">
          <ul>
            <li>
              <button
                onClick={handleSelectAllClick}
                disabled={!isSelectAllEnabled}
                className="w-full text-left px-2 py-1 text-black dark:text-gray-300 disabled:opacity-50 cursor-pointer"
              >
                {'Select All'}
              </button>
            </li>
            <li>
              <button
                onClick={handleClearSelectionClick}
                disabled={!isClearSelectionEnabled}
                className="w-full text-left px-2 py-1 text-black dark:text-gray-300 disabled:opacity-50 cursor-pointer"
              >
                {'Clear selection'}
              </button>
            </li>
          </ul>
          <ul ref={optionsListRef} className="px-2 mt-2">
            {options.map((option, index) => (
              <li key={index}>
                <CheckBox
                  className="cursor-pointer"
                  name={name}
                  value={option.value}
                  onChange={handleChange}
                  checked={selectedOptions.includes(option.value)}
                  label={option.key}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
    </div>
  );
};
